import { MenuItem, Select, Stack, Typography } from '@mui/material'
import {
  type PlaceNode,
  PlaceNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type { FC } from 'react'
import { type FlatNode, flattenNodes } from './EditPlaceGroup'
import type { useFormikProps } from './EditPlaceGroup.hooks'

type TProps = Pick<
  ReturnType<typeof useFormikProps>,
  'setFieldValue' | 'values'
> & {
  nodes: PlaceNode[]
  activeNodeId?: string
  activeParentNodeId?: number
}

const ParentRow: FC<TProps> = ({
  setFieldValue,
  values,
  nodes,
  activeNodeId,
  activeParentNodeId,
}) => {
  const placeGroupNodes = flattenNodes(nodes, []).filter(
    ({ type }) => type === PlaceNodeTypeEnum.PlaceGroup,
  )
  const activeNode = placeGroupNodes.find(({ uuid }) => uuid === activeNodeId)
  const activeParentNode = placeGroupNodes.find(
    ({ id }) => id === activeParentNodeId,
  )
  const activeRootId = activeNode?.path[0]
  const activeRootNode = placeGroupNodes.find(({ id }) => id === activeRootId)
  let activeRelationNodeIds: number[] = []
  let activeChildNodeIds: number[] = []
  if (activeRootId !== undefined && activeRootNode !== undefined) {
    // 選択された現場グループの最上位に紐づく全現場グループ
    activeRelationNodeIds = flattenNodes(
      activeRootNode.nodes as FlatNode[],
      activeNode?.path,
    ).map(({ id }) => id)
    activeRelationNodeIds.push(activeRootId)
    // 選択された現場グループに紐づく現場グループ
    activeChildNodeIds = flattenNodes(
      activeNode?.nodes as FlatNode[],
      activeNode?.path,
    ).map(({ id }) => id)
  }

  const isRoot = activeNode === undefined || activeNode.isRootNode === 1
  const isNew = activeNode === undefined && activeParentNode !== undefined
  const isExisting = activeNode !== undefined

  const defaultValue = activeParentNode
    ? activeParentNode.id.toString() || undefined
    : 'root'
  const onChange = (parentNodeId: string) =>
    setFieldValue('parentNodeId', parentNodeId)

  const availableGroups = placeGroupNodes.filter(
    ({ id }) =>
      id !== activeNode?.id &&
      activeRelationNodeIds.includes(id) &&
      !activeChildNodeIds.includes(id),
  )

  // Formik の values.parentNodeId を優先し、未設定時は defaultValue にフォールバック
  const selectValue = values.parentNodeId ?? defaultValue

  return (
    <Stack>
      <Typography variant="subtitle1" mb={1}>
        親グループ
      </Typography>
      <Select
        value={selectValue}
        disabled={isRoot}
        onChange={e => {
          const v = e.target.value as string
          onChange(v)
        }}
      >
        {isRoot && (
          <MenuItem key="root" value="root">
            最上位
          </MenuItem>
        )}
        {isNew && (
          // グループに追加する場合
          <MenuItem
            key={activeParentNode?.id.toString()}
            value={activeParentNode?.id.toString()}
          >
            {activeParentNode?.placeGroup?.name}
          </MenuItem>
        )}
        {isExisting &&
          availableGroups.map(({ id, placeGroup }) => (
            <MenuItem key={id?.toString()} value={id?.toString()}>
              {placeGroup?.name}
            </MenuItem>
          ))}
      </Select>
    </Stack>
  )
}

export default ParentRow
